import { MecordColors } from "Constants/Color";
import { useFormik } from "formik";
import ParticlesAuth from "pages/AuthenticationInner/ParticlesAuth";
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createSelector } from "reselect";
import * as Yup from "yup";
import MecordLogo from "../../assets/logo/Mecord_logo.png";
import { loginUser, resetLoginFlag } from "../../slices/thunk";
import { fetchSalesDashboardBootstrap } from "../Dashboard/services/salesIndividualDashboardApi";

const Signin = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const selectAccountAndLogin = createSelector(
    (state: any) => state.Account,
    (state: any) => state.Login,
    (account, login) => ({
      user: account.user,
      error: login.error,
      loading: login.loading,
      errorMsg: login.errorMsg,
    })
  );

  // const { user, error, loading, errorMsg } = useSelector(selectAccountAndLogin);
  const { user, error, errorMsg } = useSelector(selectAccountAndLogin);

  const [passwordShow, setPasswordShow] = useState(false);
  const [initialValues, setInitialValues] = useState({
    email: "",
    password: "",
    remember_me: false,
    // email: "metaphi@gmail.com",
    // password: "admin",
  });

  useEffect(() => {
    if (user) {
      const updatedUserData =
        process.env.REACT_APP_DEFAULTAUTH === "firebase"
          ? user.multiFactor.user.email
          : user.email;
      const updatedUserPassword =
        process.env.REACT_APP_DEFAULTAUTH === "firebase" ? "" : user.password;

      setInitialValues({
        email: updatedUserData,
        password: updatedUserPassword,
        remember_me: false,
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const data = await dispatch(loginUser(values, navigate))

        if (data?.status === 200) {
          toast.success(data?.message || "User logged in successfully", {
            toastId: "loginSuccess",
          });
          void fetchSalesDashboardBootstrap({ period: "last_month" }).catch(() => undefined);
          navigate("/dashboard");
        } else {
          toast.error(
            data?.message || "Login failed. Please try again later.",
            {
              toastId: "loginError",
            }
          );
        }
      } catch (err: any) {
        const errorMessage =
          typeof err === "string"
            ? err
            : err?.message || "Login failed. Please try again later.";
        toast.error(errorMessage, { toastId: "loginError" });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (errorMsg) {
      setTimeout(() => {
        dispatch(resetLoginFlag());
      }, 3000);
    }
  }, [dispatch, errorMsg]);

  document.title = "Login | Mecord Dashboard";

  return (
    <ParticlesAuth>
      {/* <ToastContainer/> */}
      <Col xs={12} xxl={12} className="d-flex flex-column align-items-center">
  {/* Mecord Logo */}
  <div className="text-center mt-4">
    <img
      src={MecordLogo}          // your imported logo
      alt="Mecord Logo"
      style={{ maxWidth: 100, width: "100%" }} // adjust size
      className="img-fluid"
    />
  </div>

  {/* Login Card */}
  <div style={{ maxWidth: 500, width: "100%" }}>
    <Card className="mb-0 border-0 shadow-none">
      <Card.Body className="sm-5 m-lg-4">
        <div className="text-center">
          <h5 className="fs-3xl">Welcome Back</h5>
          <p className="text-muted">
            Sign in to continue to Mecord Dashboard
          </p>
        </div>

        <div className="p-2 mt-5">
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            {/* Email Field */}
            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="Enter username"
                required
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email || ""}
                isInvalid={
                  validation.touched.email && !!validation.errors.email
                }
              />
              <Form.Control.Feedback type="invalid">
                {validation.errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Password Field */}
            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>
                Password <span className="text-danger">*</span>
              </Form.Label>
              <div className="position-relative auth-pass-inputgroup mb-3">
                <Form.Control
                  type={passwordShow ? "text" : "password"}
                  placeholder="Enter password"
                  required
                  name="password"
                  value={validation.values.password || ""}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  isInvalid={
                    validation.touched.password &&
                    !!validation.errors.password
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {validation.errors.password}
                </Form.Control.Feedback>
                <Button
                  variant="link"
                  className="position-absolute end-0 top-0 text-decoration-none text-muted d-flex align-items-center justify-content-center"
                  type="button"
                  onClick={() => setPasswordShow((prev) => !prev)}
                  style={{
                    zIndex: 2,
                    width: 35,
                    height: "100%",
                    fontSize: 15,
                    marginRight: 5,
                  }}
                >
                  <i
                    className={
                      passwordShow
                        ? "ri-eye-fill align-middle"
                        : "ri-eye-off-fill align-middle"
                    }
                  ></i>
                </Button>
              </div>
            </Form.Group>

            {/* Remember Me & Forgot Password */}
            <div className="d-flex justify-content-between align-items-center">
              <Form.Group className="mb-0">
                <Form.Check
                  type="checkbox"
                  id="rememberMe"
                  name="remember_me"
                  label="Remember me"
                  checked={validation.values.remember_me || false}
                  onChange={(e) => {
                    validation.setFieldValue(
                      "remember_me",
                      e.target.checked
                    );
                  }}
                />
              </Form.Group>

              <Link
                to={`${process.env.PUBLIC_URL}/forgot-password`}
                className="text-muted text-decoration-none"
                style={{ fontSize: "0.75rem" }}
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <div className="mt-5">
              <Button
                type="submit"
                disabled={loading}
                className="w-100"
                style={{
                  backgroundColor: MecordColors.PrimaryButtonBGColor,
                  borderColor: MecordColors.PrimaryButtonBGColor,
                  color: "#fff",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor =
                    MecordColors.PrimaryButtonHoverBGColor;
                  e.currentTarget.style.borderColor =
                    MecordColors.PrimaryButtonHoverBGColor;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    MecordColors.PrimaryButtonBGColor;
                  e.currentTarget.style.borderColor =
                    MecordColors.PrimaryButtonBGColor;
                }}
              >
                {loading && <Spinner size="sm" className="me-2" />}
                Sign In
              </Button>
            </div>
          </Form>
        </div>
      </Card.Body>
    </Card>
  </div>
</Col>

    </ParticlesAuth>
  );
};

export default Signin;


